import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";

// Template120: Museos, parques y compras (Page10 guia-londres). Todo desde backend.
// page.texts[0]=header, texts[1]=intro | sections[0]=Hyde Park, sections[1]=Museo, sections[2]=Knightsbridge/Harrods
const Template120 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	const headerText = page.texts?.[0]?.content;
	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;
	const introText = page.texts?.[1]?.content;

	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found) return found.src ?? found.url;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const heroImage = getImageSrc("knightsbridge");
	const hydeParkImage = getImageSrc("hyde-park");
	const harrodsImage = getImageSrc("harrods");
	const hydeParkSticker = getCanvasImageUrl("hyde-park-sticker");
	const museoSticker = getCanvasImageUrl("museo-sticker");

	const sections = Array.isArray(page?.sections)
		? page.sections
		: Array.isArray(page?.data?.sections)
			? page.data.sections
			: Array.isArray(page?.data?.data?.sections)
				? page.data.data.sections
				: [];
	const section0 = sections[0]; // Hyde Park
	const section1 = sections[1]; // Museo Historia Natural
	const section2 = sections[2]; // Knightsbridge y Harrods

	const actualHeaderColor = headerBackgroundColor || "#de5624";
	const contentColor = textColor || "#444";

	const h3 = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 14,
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const p = {
		fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
		fontSize: 13,
		color: contentColor,
		margin: 0,
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
					color: "inherit",
				},
			}}
		>
			{/* CABECERA */}
			{headerText && (
				<Box
					data-block-id="header"
					data-template-header
					sx={{
						backgroundColor: actualHeaderColor,
						color: headerTextColor || "#fff",
						textAlign: "center",
						padding: "15px 0",
						fontSize: "14px",
						fontWeight: 600,
						flexShrink: 0,
						position: "sticky",
						top: 0,
						zIndex: 99999,
					}}
				>
					Día 3: {headerText}
					{logoImage && !isSmallScreen && (
						<Box
							component="img"
							src={logoImage}
							alt="logo"
							sx={{
								width: isSmallScreen ? 66 : 80,
								position: "absolute",
								right: 2,
								top: 18,
								zIndex: 999999,
							}}
						/>
					)}
				</Box>
			)}

			{/* PORTADA */}
			<Box sx={{ position: "relative" }}>
				{heroImage && (
					<img
						src={heroImage}
						alt=""
						style={{ width: "100%", height: 200, objectFit: "cover" }}
					/>
				)}
				{headerText && (
					<Box
						component="h1"
						sx={{
							fontSize: isSmallScreen ? "24px" : "28px",
							textAlign: "left",
							position: "absolute",
							bottom: 0,
							margin: "10px",
							left: "12px",
							color: "#fff",
							textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
						}}
					>
						{headerText}
					</Box>
				)}
			</Box>

			{introText && (
				<Box
					data-block-id="text-1"
					sx={{
						padding: "10px 12px",
						paddingBottom: "2px",
						position: "relative",
					}}
				>
					<Box
						component="p"
						sx={{
							fontSize: 13,
							color: "#333",
							margin: 0,
							padding: 0,
						}}
					>
						{introText}
					</Box>
				</Box>
			)}

			{/* SECCIONES */}
			<Box
				sx={{
					padding: "0 12px",
					display: "flex",
					flexDirection: "column",
					gap: "12px",
					marginTop: "8px",
				}}
			>
				{/* Hyde Park (section 0) */}
				{(section0?.title || section0?.body) && (
					<Box data-block-id="text-2" sx={{ display: "flex", gap: "16px", position: "relative" }}>
						<Box
							sx={{ display: "flex", flexDirection: "column", width: "40%" }}
						>
							{hydeParkImage && (
								<img
									src={hydeParkImage}
									alt=""
									style={{
										width: "100%",
										height: 100,
										borderRadius: 12,
										objectFit: "cover",
									}}
								/>
							)}
							<Box>
								{section0?.tooltipBox?.text && (
									<Box
										sx={{
											position: "absolute",
											bottom: isSmallScreen ? 80 : 0,
											left: isSmallScreen ? 8 : 60,
											backgroundColor: "#fff8db",
											border: "2px solid #f5c400",
											borderRadius: "16px 16px 16px 0px",
											padding: "10px 12px",
											fontSize: 11,
											color: contentColor,
											boxShadow: "2px 2px 4px rgba(0,0,0,0.15)",
											maxWidth: 120,
											zIndex: 6,
										}}
									>
										{section0.tooltipBox.text}
									</Box>
								)}
								{hydeParkSticker && (
									<img
										src={hydeParkSticker}
										alt=""
										style={{
											width: 100,
											position: "absolute",
											left: isSmallScreen ? -30 : -30,
											bottom: isSmallScreen ? 0 : -20,
											zIndex: 5,
										}}
									/>
								)}
							</Box>
						</Box>
						{section0?.sabiasQue?.[0] && (
							<Box
								sx={{
									position: "absolute",
									top: 0,
									left: isSmallScreen ? "32%" : "34%",
									zIndex: 1000,
								}}
							>
								<SabiasQue
									icono={section0.sabiasQue[0].icono}
									texto={section0.sabiasQue[0].texto}
								/>
							</Box>
						)}
						<Box
							sx={{ width: "60%", display: "flex", flexDirection: "column" }}
						>
							<Box>
								{section0?.title && (
									<Box component="h3" sx={h3}>
										{section0.title}
									</Box>
								)}
								{section0?.body && (
									<Box component="p" sx={p}>
										{section0.body}
									</Box>
								)}
							</Box>
							{section0?.extraBox?.body && (
								<Box
									sx={{
										backgroundColor: "#f4e4df",
										border: "3px solid #de5624",
										borderRadius: "12px",
										width: "100%",
										padding: "4px 10px",
										marginTop: "8px",
									}}
								>
									<Box component="p" sx={{ ...p, margin: 0, fontSize: 11 }}>
										{section0.extraBox.body}
									</Box>
								</Box>
							)}
						</Box>
					</Box>
				)}

				{/* Museo de Historia Natural (section 1) */}
				{(section1?.title || section1?.body) && (
					<Box
						data-block-id="text-3"
						sx={{
							backgroundColor: "#f4e4df",
							padding: "2px 4px",
							borderRadius: "12px",
							display: "flex",
							gap: "12px",
							alignItems: "center",
							position: "relative",
						}}
					>
						<Box sx={{ paddingLeft: "8px" }}>
							{section1?.title && (
								<Box component="h3" sx={h3}>
									{section1.title}
								</Box>
							)}
							{section1?.body && (
								<Box component="p" sx={p}>
									{section1.body}
								</Box>
							)}
						</Box>
						{museoSticker && (
							<img src={museoSticker} alt="" style={{ width: 100 }} />
						)}
						{section1?.sabiasQue?.[0] && (
							<Box
								sx={{
									position: "absolute",
									top: isSmallScreen ? -40 : -12,
									right: isSmallScreen ? "60%" : "50%",
									zIndex: 10,
								}}
							>
								<SabiasQue
									icono={section1.sabiasQue[0].icono}
									texto={section1.sabiasQue[0].texto}
								/>
							</Box>
						)}
					</Box>
				)}

				{/* Knightsbridge y Harrods (section 2) */}
				{(section2?.title || section2?.body) && (
					<Box data-block-id="text-4" sx={{ display: "flex", gap: "16px", position: "relative" }}>
						{harrodsImage && (
							<img
								src={harrodsImage}
								alt=""
								style={{
									width: "40%",
									borderRadius: 12,
									objectFit: "cover",
									height: 110,
								}}
							/>
						)}
						<Box sx={{ width: harrodsImage ? "60%" : "100%" }}>
							{section2?.title && (
								<Box component="h3" sx={h3}>
									{section2.title}
								</Box>
							)}
							{section2?.body && (
								<Box component="p" sx={p}>
									{section2.body}
								</Box>
							)}
						</Box>
						{section2?.sabiasQue?.[0] && (
							<Box sx={{ position: "absolute", top: 20, left: "33%" }}>
								<SabiasQue
									icono={section2.sabiasQue[0].icono}
									texto={section2.sabiasQue[0].texto}
								/>
							</Box>
						)}
						{section2?.sabiasQue?.[1] && (
							<Box sx={{ position: "absolute", top: -20, left: "33%" }}>
								<SabiasQue
									icono={section2.sabiasQue[1].icono}
									texto={section2.sabiasQue[1].texto}
								/>
							</Box>
						)}
					</Box>
				)}
			</Box>
		</Box>
	);
};

Template120.thumbnail = "/images/template-1.png";

export default Template120;
